import moment from 'moment';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { mapMonthIndexToName } from 'utils/months';

import { MiniSuccessButton } from '@/design-system/buttons';

import { CommentForm } from './CommentForm';

const StyledComment = styled.div`
  padding: 2em 0;

  .comment-card {
    display: flex;
    grid-template-columns: fit-content auto;
    gap: 1.5em;

    &:not(:first-child) {
      border-top: 1px solid rgba(0, 0, 0, 0.1);
    }

    .content {
      width: 100%;
    }

    .header {
      display: flex;
      justify-content: space-between;

      .author-name {
        text-transform: capitalize;
        margin-bottom: 0em;
        font-size: 1.85rem;
        font-weight: bold;
        line-height: 1.5;
      }

      .date {
        opacity: 0.7;
        font-size: 1.25rem;
      }

      .reply-trigger {
        padding: 0.5em 1.5em;
        border-radius: 5px;
        font-size: 1.25rem;
        background-color: ${(props) => props.theme.primaryLink};
        color: white;
      }
    }
  }

  .user-image-container {
    width: 50px;
    height: 50px;
    position: relative;
    overflow: hidden;
    border-radius: 50%;

    .img {
      object-fit: cover;
      width: 100%;
      position: absolute;
    }
  }

  .reply-section {
    margin-left: 2.5em;
  }
`;

const normalizeTimeUnit = (value) => {
  return value.toString().length == 1 ? `0${value}` : value;
};

const composeDatetime = (date) => {
  const momentInstance = moment(date);
  const year = momentInstance.toDate().getFullYear();
  const monthIndex = momentInstance.get('month');
  const dayInMonth = momentInstance.get('date');
  const hour = normalizeTimeUnit(momentInstance.get('hour'));
  const minute = normalizeTimeUnit(momentInstance.get('minute'));

  return `${mapMonthIndexToName(monthIndex)} ${dayInMonth}, ${year} at ${hour}:${minute}`;
};

export const Comment = ({ auth, className, comment, allowReply, onReply, children }) => {
  const [author, setAuthor] = useState(null);
  const [showCommentForm, setShowCommentForm] = useState(false);

  useEffect(() => {
    let { user, fullname, email } = comment;

    setAuthor({
      fullname: user?.fullname || fullname,
      email: user?.email || email,
      imageUrl: user?.imageUrl
    });
  }, []);

  return (
    <StyledComment className={className}>
      <div className="comment-card">
        <div className="user-image-container">
          <Image
            alt={author?.fullname}
            src={author?.imageUrl || '/images/avatar.jpg'}
            layout="fill"
            className="img"
          />
        </div>

        <div className="content">
          <div className="header">
            <div>
              <h2 className="author-name">{author?.fullname}</h2>
              <p className="date">{composeDatetime(comment._createdAt)}</p>
            </div>
            {allowReply && (
              <MiniSuccessButton
                className="reply-trigger"
                onClick={() => setShowCommentForm(!showCommentForm)}>
                Reply
              </MiniSuccessButton>
            )}
          </div>
          <div className="body">{comment.body}</div>
        </div>
      </div>

      <div className="reply-section">
        {children}
        {allowReply && showCommentForm && (
          <div className="comment-box-container">
            <CommentForm
              auth={auth}
              articleId={comment.articleId}
              commentId={comment._id}
              onSubmit={onReply}
            />
          </div>
        )}
      </div>
    </StyledComment>
  );
};
